import { Injectable } from '@angular/core'
import { combineLatest, filter, firstValueFrom, switchMap, take } from 'rxjs'
import { tap } from 'rxjs/operators'

import { AuthService } from '../../shared/services/auth.service'
import { BalanceService } from '../../shared/services/balance.service'
import { BetService } from '../../shared/services/bet.service'
import { BetConfigService } from '../../shared/services/bet-config.service'
import { HelperService } from '../../shared/services/helper.service'
import { LiveUpdateService } from '../../shared/services/live-update.service'
import { WebsocketService } from '../../shared/services/websocket.service'
import { BetSlipSetting } from '../../shared/types/auth.type'

@Injectable({
	providedIn: 'root',
})
export class AuthenticatedLayoutService {
	constructor(
		private readonly helperService: HelperService,
		private readonly balanceService: BalanceService,
		private readonly authService: AuthService,
		private readonly websocketService: WebsocketService,
		private readonly liveUpdateService: LiveUpdateService,
		private readonly betService: BetService,
		private readonly betConfigService: BetConfigService,
	) {}

	initializeApp() {
		this.initWebsocketConnection()
		this.balanceService.getBalance()
		this.subscribeToWebsocketEvents()
		this.refreshExposureOnWebsocketConnection()
		this.onUserOnline()
	}

	onUserOnline = () => {
		window.addEventListener('online', () => {
			setTimeout(() => {
				this.refreshExposureOnWebsocketConnection()
			}, 2000)
		})
	}

	private initWebsocketConnection() {
		// Initial connection
		this.websocketService.startConnection().subscribe({
			error: (error) => console.error('Initial WebSocket connection failed:', error),
		})

		// Auto-reconnection when connection is lost
		this.websocketService.connectionStatus$
			.pipe(
				filter((status) => !status && this.authService.getAccessToken() !== ''),
				switchMap(() => this.websocketService.startConnection()),
			)
			.subscribe({
				error: (error) => console.error('WebSocket reconnection failed:', error),
			})
	}

	private subscribeToWebsocketEvents() {
		const userData = this.helperService.getUserData()

		this.websocketService.connectionStatus$
			.pipe(
				filter((status) => status && Boolean(userData)),
				switchMap(() =>
					this.liveUpdateService.getData<{
						type: string
						markets?: string
						data?: {
							id?: string | number
							DOMAIN_ID?: string
							DOMAIN_NAME?: string
							balance?: string
							risk_exposure?: string
							liability?: number
							total_exposure?: string
						}
					}>(),
				),
				// debounceTime(WEBSOCKET_DEBOUNCE_MILLISECONDS),
			)
			.subscribe({
				next: (update) => {
					if (update?.type === 'wl_betslip' && update.markets && userData?.user_fields?.DOMAIN_NAME) {
						const parsedData = JSON.parse(update.markets) as { betslip: BetSlipSetting; domain_name: string }
						this.helperService.patchUserFields({ WHITE_LABEL_BET_SLIP: parsedData.betslip })
					}

					if (update?.type === 'wallet') {
						const data = update.data as { id?: string | number; balance: string; risk_exposure: string; liability: number; total_exposure: string }
						const walletId = this.helperService.getWalletId()

						if (data?.id && String(data.id) === walletId) {
							this.balanceService.balanceData$.next({
								...this.balanceService.balanceData$.getValue()!,
								BALANCE: data.balance,
								RISK_EXPOSURE: data.risk_exposure,
								LIABILITY: data.liability,
								TOTAL_EXPOSURE: data.total_exposure,
							})
							this.balanceService.isExposureFromWebSocket$.next(true)
						}

						firstValueFrom(this.betService.fetchCurrentUserOrders())
					}

					if (update?.type === 'logout') {
						this.handleLogout(update)
					}

					if (update?.type === 'bet-config') {
						const betConfigData = (update as { type: string; data?: { BET_CONFIG?: string } }).data?.BET_CONFIG
						if (betConfigData) this.betConfigService.updateBetConfig(betConfigData)
					}

					if (update?.type === 'wl-permissions') {
						if (this.isDomainMatch(update.data?.DOMAIN_ID)) {
							const wlPerms: { is_active: boolean; permission: string }[] = (update as any).data?.WL_PERMISSIONS ?? []
							const newPermissions = wlPerms
								.filter((p: { is_active: boolean; permission: string }) => p.is_active)
								.map((p: { is_active: boolean; permission: string }) => p.permission)
								.join(',')
							this.helperService.patchUserFields({ PERMISSIONS: newPermissions })
						}
					}

					if (update?.type === 'casino-user-status') {
						const payload = (update as any).data
						if (this.isCurrentUser(payload?.USER_IDS ?? [])) {
							const blocked = payload?.IS_BLOCKED_CASINO === true
							if (this.helperService.patchUserFields({ IS_BLOCKED_CASINO: blocked ? 'true' : 'false' })) {
								this.authService.isCasinoBlocked$.next(blocked)
							}
						}
					}
				},
				error: (error) => {
					console.error('WebSocket error:', error)
					this.initializeApp()
				},
			})
	}

	private handleLogout(payload: { type: string; data?: { DOMAIN_ID?: string; DOMAIN_NAME?: string } }) {
		if (this.isDomainMatch(payload?.data?.DOMAIN_ID)) {
			this.authService.logOut()
		}
	}

	private isDomainMatch(domainId?: string): boolean {
		if (!domainId) return true
		const userDomainId = this.helperService.getUserData()?.user_fields?.DOMAIN_ID
		return !!userDomainId && String(domainId) === String(userDomainId)
	}

	private isCurrentUser(userIds: string[]): boolean {
		const userId = this.helperService.getUserData()?.user_fields?.ID
		return !!userId && userIds.includes(String(userId))
	}

	private refreshExposureOnWebsocketConnection() {
		combineLatest([this.balanceService.balanceData$, this.websocketService.connectionStatus$])
			.pipe(
				filter(([balanceData, status]) => status && Boolean(balanceData)),
				take(1),
				switchMap(([balanceData]) => this.balanceService.refreshExposure(balanceData!.ID.toString())),
				tap(() => (this.balanceService.isFetchingBalance = false)),
			)
			.subscribe()
	}
}
